import {
  getPricingSummary,
  getVulcanPriceSelections
} from '../vulcanPricing/vulcanPricing';

/**
 * Factory for the engine's `pricing` adapter (FR-018).
 *
 * `getPricingSummary`/`getVulcanPriceSelections` (vulcanPricing.js) are 100%
 * product-specific pricing calculation — the research.md decision was to wrap
 * them behind a single `context` object, not to preserve or generalize their
 * 9-positional-argument shape.
 *
 * v2 signature: the engine calls `pricing(fieldStates, context)`. The engine's
 * rich per-field state map (`fieldStates` = `{ [name]: { value, isVisible,
 * options, ...+ fieldRules extras like monthlyPayment } }`) IS the shape the
 * pricing functions want, so it is the field map by default. During the Stage-3
 * transition the app may still pass its own `formFields` via
 * `adapterContext.formFields`, which takes precedence.
 *
 * The six ancillary section blobs (redeemOfferData, businessOptikTvTableData,
 * officePhoneLinesData, fibreInternetData, copperInternetData, tsbData) now live
 * in the engine's ONE STATE BAG and arrive via `context.values`, not as separate
 * context keys — matching how the section registry writes them
 * (`form.setValue(KEY, …)`).
 *
 * NOTE (finalized at Stage 3): the equipment section's NESTED sub-field state
 * (e.g. `${package}additionalEquipmentVulcan` → `{ [product]: { value } }`) is
 * not a leaf field, so it is not in `fieldStates`; it lives as an ancillary blob
 * in `context.values`. The final field map merges `fieldStates` with those
 * nested blobs once the equipment section is built.
 *
 * @returns {import('@mobilelive-inc/telus-forms-core').PricingAdapter}
 */
export function createVulcanPricingAdapter() {
  return (fieldStates, context) => {
    const { values = {}, formFields, orderData, alternateLanguage, step } =
      context || {};
    // The ancillary section blobs default to `{}` — in the app they are always
    // initialized, but in the engine they are undefined until a bespoke section
    // writes them, and the pricing helpers read `.data`/selection fields off them
    // without null-guards (e.g. getConsolidationSelectionTotals reads
    // `stateData.data`). An empty object yields zero totals (nothing selected)
    // instead of throwing.
    const {
      redeemOfferData = {},
      businessOptikTvTableData = {},
      officePhoneLinesData = {},
      fibreInternetData = {},
      copperInternetData = {},
      tsbData = {}
    } = values;

    // The rich field map: the app's formFields during transition, else the
    // engine's fieldStates. Nothing to price before either exists.
    const fields = formFields || fieldStates;
    if (!fields) return undefined;

    return {
      summary: getPricingSummary(
        fields,
        alternateLanguage,
        redeemOfferData,
        businessOptikTvTableData,
        officePhoneLinesData,
        fibreInternetData,
        copperInternetData,
        tsbData,
        orderData
      ),
      selections: getVulcanPriceSelections(
        fields,
        redeemOfferData,
        orderData,
        businessOptikTvTableData,
        officePhoneLinesData,
        fibreInternetData,
        copperInternetData,
        tsbData,
        step
      )
    };
  };
}
